import { TrackSkeleton } from "../track-skeleton";
import { lineConfig, pointNames } from "@/shared/consts";
import { useViewSettings } from "@/view-settings";
import { observer } from "@legendapp/state/react";
import { useReprojectionsBuffer } from "@/services/buffer-controller";
import { useTimelinePlayback } from "@/contexts/playback-context";
import { getUniqueColor } from "@/shared";
import { useMemo } from "react";
import { getDateTime } from "@/services/buffer-controller/utils";

interface ReprojectionProps {
  trackId: string;
  debug?: boolean;
}

export const Reprojection = observer(({ trackId }: ReprojectionProps) => {
  const projectionsBuffer = useReprojectionsBuffer();
  const timelinePlayback = useTimelinePlayback();
  const viewSettings = useViewSettings();
  const currentTime = timelinePlayback.currentTime.get();
  const snapshot = projectionsBuffer.getSnapshotToDraw(trackId, currentTime);
  const color = useMemo(() => getUniqueColor(trackId), [trackId]);
  const confidence = viewSettings.reprojectionConfidence.get();

  if (!snapshot) return null;

  const track = {
    pose_x: snapshot.points.map((pose) => (16 * pose[0]) / 1920 - 8),
    pose_y: snapshot.points.map((pose) => (9 * pose[1]) / 1080 - 4.5),
    pose_z: snapshot.points.map(() => 0),
    pose_conf: snapshot.points.map((pose) => pose[2]),
  };

  const shouldSkipPoint = (index: number) => {
    const isEnabled = viewSettings.showOverflowPoints.peek();

    if (!isEnabled) {
      const [x, y] = snapshot.points[index];
      return x < 0 || x > 1920 || y < 0 || y > 1080;
    }

    return false;
  };

  const pointPopupContent = (selectedIndex: number) => (
    <ul>
      <li className="whitespace-nowrap">Name: {pointNames[selectedIndex]}</li>
      <li className="whitespace-nowrap">
        X: {snapshot.points[selectedIndex][0]}
      </li>
      <li className="whitespace-nowrap">
        Y: {snapshot.points[selectedIndex][1]}
      </li>
      <li className="whitespace-nowrap">
        Conf: {snapshot.points[selectedIndex][2]}
      </li>
      <li className="whitespace-nowrap">
        Projection time: {getDateTime(snapshot)}
      </li>
    </ul>
  );

  return (
    <>
      <TrackSkeleton
        lines={lineConfig}
        lineWidth={0.05}
        color={color.string}
        pointSize={0.2}
        dashed
        distanceFactor={10}
        confidence={confidence}
        track={track}
        shouldSkipPoint={shouldSkipPoint}
        pointPopupContent={pointPopupContent}
      />
    </>
  );
});
